import React, { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  ArrowLeft,
  Camera,
  Sparkles,
  User,
  Briefcase,
  Phone,
  CheckCircle2,
  Clock,
  Building2,
  Shield,
} from 'lucide-react'
import DefaultImagePNG from '../../../../assets/images/DefaultProfilePic.png'
import { useAppDispatch, useAppSelector } from '../../../../Redux/Hooks'
import { createNewMemberAPI, getAllMembers } from '../../../../Redux/ApiCalls/Dashboard/MembersAPI'
import { getAllRolesAPI, assignRoleAPI } from '../../../../Redux/ApiCalls/Dashboard/RoleApi'
import { usePermissions } from '../../../../hooks/usePermissions'
import LoadingDots from '../../../atoms/Utlis/LoadinDots'
import imageCompression from 'browser-image-compression'
import {
  PageContainer,
  Hero,
  HeroGlow,
  HeroRow,
  HeroLeft,
  BackButton,
  HeroText,
  HeroBadge,
  PageTitle,
  PageSubtitle,
  ProgressWrap,
  ProgressLabel,
  ProgressPct,
  ProgressTrack,
  ProgressFill,
  ProgressHint,
  ScrollBody,
  FormGrid,
  FormCard,
  CardHeader,
  CardIcon,
  CardTitle,
  CardSubtitle,
  PhotoUploadArea,
  PhotoRing,
  MemberImage,
  PhotoBadge,
  PhotoHint,
  PhotoSubhint,
  FieldGrid,
  Field,
  FieldFull,
  Label,
  RequiredStar,
  FieldHint,
  Input,
  Select,
  PhoneRow,
  CountryCodeBox,
  CountryCodeInput,
  PhoneInput,
  ErrorSpan,
  SubmitBar,
  SubmitHint,
  SubmitButton,
  JOB_TYPES,
  EMPLOYMENT_TYPES,
  getFormProgress,
  FormLabelIcon,
  FormGradientDefs,
} from './memberFormStyles'

const CreateNewMemberPage: React.FC = () => {
  const [name, setName] = useState<string>('')
  const [jobType, setJobType] = useState<string>('')
  const [employmentType, setEmploymentType] = useState<string>('Full Time')
  const navigate = useNavigate()
  const [selectedImage, setSelectedImage] = useState<File>()
  const [imagePreview, setImagePreview] = useState<string>('')
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [countryCode, setCountryCode] = useState<string>('+91')
  const [contact, setContact] = useState<string>('')
  const [activeButton, setActiveButton] = useState<boolean>(false)
  const [showError, setShowError] = useState<boolean>(false)
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
  const dispatch = useAppDispatch()
  const { loading } = useAppSelector((state) => state.member)
  const { branches, selectedBranch } = useAppSelector((state) => state.auth)
  const { roles, isRolesFetched } = useAppSelector((state) => state.role)
  const [branchId, setBranchId] = useState<number | string>(selectedBranch?.id || '')
  const [assignedRoleId, setAssignedRoleId] = useState<number | string>('')

  const { hasAnyPermission, permissionsLoading } = usePermissions()
  const canAssignRole = hasAnyPermission(['role_management.read', 'role_management.update'])

  const prevSelectedBranchIdRef = useRef<number | null | undefined>(selectedBranch?.id)

  useEffect(() => {
    if (prevSelectedBranchIdRef.current !== selectedBranch?.id) {
      prevSelectedBranchIdRef.current = selectedBranch?.id
      setBranchId(selectedBranch?.id ? String(selectedBranch.id) : '')
    }
  }, [selectedBranch?.id])

  const progress = getFormProgress({
    name,
    jobType,
    contact,
    countryCode,
    hasPhoto: !!imagePreview,
  })

  useEffect(() => {
    if (canAssignRole && !permissionsLoading && !isRolesFetched && roles.length === 0) {
      dispatch(getAllRolesAPI())
    }
  }, [canAssignRole, permissionsLoading, isRolesFetched, roles.length, dispatch])

  useEffect(() => {
    isValidData()
  }, [selectedImage, name, jobType, employmentType, contact, countryCode])

  const handleDivClick = () => fileInputRef.current?.click()

  const blobToFile = (blob: Blob, fileName: string): File =>
    new File([blob], fileName, { type: blob.type })

  const compressImage = async (file: File): Promise<Blob> => {
    return imageCompression(file, {
      maxSizeMB: 1,
      maxWidthOrHeight: 1920,
      useWebWorker: false,
    })
  }

  const handleImageChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files?.[0]) return
    let file = event.target.files[0]
    if (file.size / 1024 / 1024 > 2) {
      file = blobToFile(await compressImage(file), file.name)
    }
    const reader = new FileReader()
    reader.readAsDataURL(file)
    reader.onloadend = () => {
      setSelectedImage(file)
      setImagePreview(reader.result as string)
    }
  }

  const validatePhoneNumber = (phoneNumber: string): boolean =>
    /^[0-9]{10}$/.test(phoneNumber)

  const isValidData = () => {
    const valid =
      name.length >= 4 &&
      name.length <= 25 &&
      jobType.length > 0 &&
      validatePhoneNumber(contact) &&
      countryCode.length > 0 &&
      countryCode.length <= 4
    setActiveButton(valid)
    if (valid) setShowError(false)
  }

  const handleSubmit = async () => {
    if (!activeButton) {
      setShowError(true)
      return
    }
    const formData = new FormData()
    formData.append('country_code', countryCode)
    formData.append('phone_number', contact)
    formData.append('job_type', jobType)
    formData.append('employment_type', employmentType)
    formData.append('name', name)
    if (branchId) {
      formData.append('branch_id', String(branchId))
    }
    if (selectedImage) {
      formData.append('profile_image', selectedImage)
    } else {
      const defaultImageBlob = await fetch(DefaultImagePNG).then((res) => res.blob())
      formData.append('profile_image', defaultImageBlob, 'default.png')
    }

    try {
      setIsSubmitting(true)
      const res = await dispatch(createNewMemberAPI(formData)).unwrap()
      const returnedId = res?.id || res?.data?.id || res?.result?.id || res?.data?.data?.id

      let memberIdToAssign = returnedId
      if (!memberIdToAssign) {
        const refreshed = await dispatch(getAllMembers()).unwrap()
        if (Array.isArray(refreshed)) {
          const found = refreshed.find((m: any) => m.phone_number === contact.trim())
          if (found) memberIdToAssign = found.id
        }
      }

      if (canAssignRole && assignedRoleId && memberIdToAssign) {
        try {
          await dispatch(
            assignRoleAPI({
              roleId: assignedRoleId,
              teamMemberId: memberIdToAssign,
            })
          ).unwrap()
          if (canAssignRole) dispatch(getAllRolesAPI())
        } catch (roleErr: any) {
          console.error('Failed to assign role to new member:', roleErr)
        }
      }

      dispatch(getAllMembers())
      navigate('/dashboard/members/all')
    } catch (err: any) {
      // Error is handled in reducer / toast
    } finally {
      setIsSubmitting(false)
    }
  }

  const submitHint =
    progress >= 80
      ? 'Looking great! Hit submit to add this member to your team.'
      : progress >= 40
        ? "Keep going — a few more details and you're done."
        : 'Start with a photo and name to build your team profile.'

  return (
    <PageContainer>
      <FormGradientDefs />
      <Hero>
        <HeroGlow />
        <HeroRow>
          <HeroLeft>
            <BackButton type="button" onClick={() => navigate(-1)} aria-label="Go back">
              <ArrowLeft size={18} />
            </BackButton>
            <HeroText>
              <div className="flex flex-col">
                <HeroBadge>
                  <Sparkles size={12} />
                  Build your team
                </HeroBadge>
                <PageTitle className="linear-text">Add Member</PageTitle>
                <PageSubtitle>
                  Add a new team member — photographers, editors, and studio staff all in one place.
                </PageSubtitle>
              </div>
            </HeroText>
          </HeroLeft>
          <ProgressWrap>
            <ProgressLabel>
              Profile completion
              <ProgressPct>{progress}%</ProgressPct>
            </ProgressLabel>
            <ProgressTrack>
              <ProgressFill $pct={progress} />
            </ProgressTrack>
            <ProgressHint>
              {progress === 100 ? 'Ready to submit!' : `${5 - Math.round(progress / 20)} fields left`}
            </ProgressHint>
          </ProgressWrap>
        </HeroRow>
      </Hero>

      <ScrollBody>
        <FormGrid>
          <FormCard>
            <CardHeader>
              <CardIcon>
                <User size={20} />
              </CardIcon>
              <div>
                <CardTitle>Profile photo</CardTitle>
                <CardSubtitle>A friendly face helps your team feel connected</CardSubtitle>
              </div>
            </CardHeader>
            <PhotoUploadArea onClick={handleDivClick}>
              <input
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                style={{ display: 'none' }}
                ref={fileInputRef}
              />
              <PhotoRing>
                <MemberImage src={imagePreview || DefaultImagePNG} alt="Member" />
                <PhotoBadge>
                  <Camera size={16} />
                </PhotoBadge>
              </PhotoRing>
              <PhotoHint>{imagePreview ? 'Tap to change photo' : 'Upload a profile photo'}</PhotoHint>
              <PhotoSubhint>JPG or PNG · Max 2 MB · Optional but recommended</PhotoSubhint>
            </PhotoUploadArea>
            <Field>
              <Label>
                <FormLabelIcon icon={User} />
                Full name<RequiredStar>*</RequiredStar>
              </Label>
              <FieldHint>How this member appears across your studio</FieldHint>
              <Input
                value={name}
                onChange={(e) => {
                  if (e.target.value.length <= 25) setName(e.target.value)
                }}
                placeholder="e.g. Rahul Sharma"
              />
              {!(name.length >= 2) && showError && (
                <ErrorSpan>Please enter a member name (at least 4 characters).</ErrorSpan>
              )}
            </Field>
          </FormCard>

          <FormCard>
            <CardHeader>
              <CardIcon $tone="#35b7c8">
                <Briefcase size={20} />
              </CardIcon>
              <div>
                <CardTitle>Role & contact</CardTitle>
                <CardSubtitle>Assign their role and how to reach them</CardSubtitle>
              </div>
            </CardHeader>
            <FieldGrid>
              <FieldFull>
                <Label>
                  <FormLabelIcon icon={Briefcase} />
                  Employment type<RequiredStar>*</RequiredStar>
                </Label>
                <Select
                  value={jobType || 'Job Type'}
                  onChange={(e) => setJobType(String(e.target.value))}
                >
                  <option value="Job Type" disabled>
                    Select a type
                  </option>
                  {JOB_TYPES.map((role) => (
                    <option key={role} value={role}>
                      {role}
                    </option>
                  ))}
                </Select>
                {!(jobType.length > 0) && showError && (
                  <ErrorSpan>Please select a job type.</ErrorSpan>
                )}
              </FieldFull>
              {canAssignRole && (
                <FieldFull>
                  <Label>
                    <FormLabelIcon icon={Shield} />
                    Assign Role
                  </Label>
                  <FieldHint>Assign dashboard permissions and login access </FieldHint>
                  <Select
                    value={assignedRoleId}
                    onChange={(e) => setAssignedRoleId(e.target.value)}
                  >
                    <option value="">-- No custom role (No dashboard access) --</option>
                    {roles.map((r) => (
                      <option key={r.id} value={r.id}>
                        {r.name} ({r.permissions?.length || 0} permissions)
                      </option>
                    ))}
                  </Select>
                  {assignedRoleId && (
                    <FieldHint style={{ color: '#c94daa', marginTop: '4px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <Sparkles size={12} color="#c94daa" />
                      <span>
                        {roles.find((r) => String(r.id) === String(assignedRoleId))?.description ||
                          "Auto-creates sub-user login access with assigned permissions."}
                      </span>
                    </FieldHint>
                  )}
                </FieldFull>
              )}
              <FieldFull>
                <Label>
                  <FormLabelIcon icon={Building2} />
                  Branch
                </Label>
                <FieldHint>Assign member to a specific studio branch (optional)</FieldHint>
                <Select
                  value={branchId}
                  onChange={(e) => setBranchId(e.target.value)}
                >
                  <option value="">All Branches / Studio HQ (No branch)</option>
                  {branches.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.branch_name} ({b.branch_city})
                    </option>
                  ))}
                </Select>
              </FieldFull>
              <FieldFull>
                <Label>
                  <FormLabelIcon icon={Phone} />
                  Phone number<RequiredStar>*</RequiredStar>
                </Label>
                <FieldHint>For assignments, WhatsApp shares & login</FieldHint>
                <PhoneRow>
                  <CountryCodeBox>
                    <Phone size={16} color="#c94daa" />
                    <CountryCodeInput
                      value={countryCode}
                      onChange={(e) => {
                        if (e.target.value.length <= 4) setCountryCode(e.target.value)
                      }}
                      placeholder="+91"
                    />
                  </CountryCodeBox>
                  <PhoneInput
                    value={contact}
                    onChange={(e) => {
                      const digitsOnly = e.target.value.replace(/\D/g, '')
                      if (digitsOnly.length <= 10) setContact(digitsOnly)
                    }}
                    placeholder="10-digit mobile number"
                    type="tel"
                    inputMode="numeric"
                  />
                </PhoneRow>
                {!(contact.length === 10 && validatePhoneNumber(contact)) && showError && (
                  <ErrorSpan>Please enter a valid 10-digit phone number.</ErrorSpan>
                )}
                {!(countryCode.length > 0 && countryCode.length <= 4) && showError && (
                  <ErrorSpan>Please provide a country code.</ErrorSpan>
                )}
              </FieldFull>
              <FieldFull>
                <Label>
                  <FormLabelIcon icon={Clock} />
                  Employment type<RequiredStar>*</RequiredStar>
                </Label>
                <Select
                  value={employmentType}
                  onChange={(e) => setEmploymentType(e.target.value)}
                >
                  {EMPLOYMENT_TYPES.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </Select>
              </FieldFull>
            </FieldGrid>
          </FormCard>
        </FormGrid>

        <SubmitBar>
          <SubmitHint>{submitHint}</SubmitHint>
          {loading || isSubmitting ? (
            <LoadingDots />
          ) : (
            <SubmitButton type="button" $ready={activeButton} onClick={handleSubmit}>
              {activeButton && <CheckCircle2 size={18} />}
              Add member
            </SubmitButton>
          )}
        </SubmitBar>
      </ScrollBody>
    </PageContainer>
  )
}

export default CreateNewMemberPage
